/**
 * Armadillo Adamantium API Service Layer
 * Phoenix Architecture: Database → API Service → Components
 * 
 * This service provides a clean interface between the database
 * and UI components, following the Phoenix Architecture pattern.
 */

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface TrustScore {
  id: string;
  user_id: string;
  score: number;
  tier: number;
  tier_name: string;
  last_updated: string;
  created_at: string;
}

export interface TrustHistoryEntry {
  id: string;
  user_id: string;
  previous_score: number | null;
  new_score: number | null;
  delta: number | null;
  reason: string | null;
  created_at: string;
}

export interface SecurityEvent {
  id: string;
  user_id: string | null;
  event_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  metadata: Record<string, any> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface ThreatEvent {
  id: string;
  user_id: string | null;
  ip: string | null;
  threat_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string | null;
  url: string | null;
  method: string | null;
  user_agent: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
}

export interface RateLimitBucket {
  id: string;
  identifier: string;
  token_count: number;
  last_refill: string;
  created_at: string;
}

// ============================================
// TRUST SCORE API
// ============================================

/**
 * Get user's current trust score
 */
export const getTrustScore = async (userId: string): Promise<TrustScore | null> => {
  try {
    const { data, error } = await supabase
      .from('trust_scores')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Failed to get trust score:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching trust score:', error);
    return null;
  }
};

/**
 * Initialize trust score for new user
 */
export const initializeTrustScore = async (userId: string): Promise<TrustScore | null> => {
  try {
    const initialScore = parseInt(import.meta.env.VITE_ARMADILLO_INITIAL_TRUST_SCORE || '25');
    
    const { data, error } = await supabase
      .from('trust_scores')
      .insert({
        user_id: userId,
        score: initialScore,
        tier: 1,
        tier_name: 'Probationary'
      })
      .select()
      .maybeSingle();

    if (error) {
      console.error('Failed to initialize trust score:', error);
      return null;
    }

    console.log(`🦔 Trust score initialized: ${initialScore} (Probationary)`);
    return data;
  } catch (error) {
    console.error('Error initializing trust score:', error);
    return null;
  }
};

/**
 * Update user's trust score
 */
export const updateTrustScore = async (
  userId: string,
  newScore: number,
  reason: string
): Promise<boolean> => {
  try {
    // Get current score
    const current = await getTrustScore(userId);
    if (!current) {
      await initializeTrustScore(userId);
      return false;
    }

    const previousScore = current.score;
    const delta = newScore - previousScore;

    // Update trust score
    const { error: updateError } = await supabase
      .from('trust_scores')
      .update({
        score: Math.max(0, Math.min(100, newScore)),
        tier: calculateTier(newScore).tier,
        tier_name: calculateTier(newScore).name,
        last_updated: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (updateError) {
      console.error('Failed to update trust score:', updateError);
      return false;
    }

    // Log in history
    await logTrustHistory(userId, previousScore, newScore, delta, reason);

    logger.info(`🦔 Trust score updated: ${previousScore} → ${newScore} (${delta > 0 ? '+' : ''}${delta}) - ${reason}`);
    return true;
  } catch (error) {
    console.error('Error updating trust score:', error);
    return false;
  }
};

/**
 * Get user's trust history
 */
export const getTrustHistory = async (userId: string, limit: number = 50): Promise<TrustHistoryEntry[]> => {
  try {
    const { data, error } = await supabase
      .from('trust_history')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Failed to get trust history:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching trust history:', error);
    return [];
  }
};

/**
 * Log trust score change to history
 */
const logTrustHistory = async (
  userId: string,
  previousScore: number,
  newScore: number,
  delta: number,
  reason: string
): Promise<void> => {
  try {
    await supabase
      .from('trust_history')
      .insert({
        user_id: userId,
        previous_score: previousScore,
        new_score: newScore,
        delta,
        reason
      });
  } catch (error) {
    console.error('Error logging trust history:', error);
  }
};

// ============================================
// SECURITY EVENTS API
// ============================================

/**
 * Log a security event
 */
export const logSecurityEvent = async (
  userId: string | null,
  eventType: string,
  severity: 'low' | 'medium' | 'high' | 'critical',
  message: string,
  metadata?: Record<string, any>
): Promise<boolean> => {
  try {
    // Prefer the server API — it captures the real client IP. Fall back to a
    // direct insert (third-party IP lookup) if the API is unreachable.
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      try {
        const res = await fetch(`/api/armadillo?action=log-event`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ eventType, severity, message, metadata }),
        });
        if (res.ok) return true;
      } catch {
        // fall through to direct insert
      }
    }
    const { error } = await supabase
      .from('security_events')
      .insert({
        user_id: userId,
        event_type: eventType,
        severity,
        message,
        metadata: metadata || null,
        ip_address: await getClientIP(),
        user_agent: navigator.userAgent
      });

    if (error) {
      console.error('Failed to log security event:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error logging security event:', error);
    return false;
  }
};

/**
 * Get user's security events
 */
export const getSecurityEvents = async (
  userId: string,
  limit: number = 100
): Promise<SecurityEvent[]> => {
  try {
    const { data, error } = await supabase
      .from('security_events')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Failed to get security events:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching security events:', error);
    return [];
  }
};

// ============================================
// THREAT DETECTION API
// ============================================

/**
 * Log a detected threat
 */
export const logThreat = async (
  userId: string | null,
  threatType: string,
  severity: 'low' | 'medium' | 'high' | 'critical',
  description?: string,
  metadata?: Record<string, any>
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('threat_events')
      .insert({
        user_id: userId,
        threat_type: threatType,
        severity,
        description: description || null,
        metadata: metadata || null,
        ip: await getClientIP(),
        user_agent: navigator.userAgent
      });

    if (error) {
      console.error('Failed to log threat:', error);
      return false;
    }

    console.warn(`⚠️ Threat detected: ${threatType} (${severity})`);
    return true;
  } catch (error) {
    console.error('Error logging threat:', error);
    return false;
  }
};

export interface ThreatCheckOptions {
  /** Disable only telemetry for offline callers; detection remains unchanged. */
  log?: boolean;
}

/**
 * Check if input contains SQL injection patterns
 */
export const checkSQLInjection = async (input: string, userId: string | null, options: ThreatCheckOptions = {}): Promise<boolean> => {
  const sqlPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER)\b)/i,
    /(--|\/\*|\*\/|;)/,
    /(\bOR\b\s+\d+=\d+)/i
  ];

  const isThreat = sqlPatterns.some(pattern => pattern.test(input));
  
  if (isThreat && options.log !== false) {
    await logThreat(userId, 'sql_injection', 'critical', 'Potential SQL injection detected', {
      input_length: input.length
    });
  }

  return isThreat;
};

/**
 * Check if input contains XSS patterns
 */
export const checkXSS = async (input: string, userId: string | null, options: ThreatCheckOptions = {}): Promise<boolean> => {
  const xssPatterns = [
    /<script[^>]*>/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe[^>]*>/i,
    /<object[^>]*>/i
  ];

  const isThreat = xssPatterns.some(pattern => pattern.test(input));
  
  if (isThreat && options.log !== false) {
    await logThreat(userId, 'xss_attack', 'high', 'Potential XSS attack detected', {
      input_length: input.length
    });
  }

  return isThreat;
};

/**
 * Check if input contains path traversal patterns
 */
export const checkPathTraversal = async (input: string, userId: string | null, options: ThreatCheckOptions = {}): Promise<boolean> => {
  const pathPatterns = [
    /\.\.\//,  // ../
    /\.\.\\/,  // ..\
    /%2[eE]%2[eE]/,  // URL encoded ../
    /%252[eE]%252[eE]/,  // Double URL encoded
    /\.\.%2[fF]/,  // ..%2F
    /\.\.%5[cC]/,  // ..%5C
  ];

  const isThreat = pathPatterns.some(pattern => pattern.test(input));
  
  if (isThreat && options.log !== false) {
    await logThreat(userId, 'path_traversal', 'high', 'Potential path traversal attack detected', {
      input_length: input.length
    });
  }

  return isThreat;
};

/**
 * Check if input contains command injection patterns
 */
export const checkCommandInjection = async (input: string, userId: string | null, options: ThreatCheckOptions = {}): Promise<boolean> => {
  // NOTE: Bare shell metacharacters (&, $, ;, |) appear constantly in
  // legitimate text ("Fish & Chips", "$10 budget", "noon; then after") and
  // must NOT be flagged on their own. Only flag real injection shapes:
  // $(...), backticks, &&/|| chaining, or a shell command word after a
  // separator.
  const cmdPatterns = [
    /\$\(/,  // Command substitution $(...)
    /`[^`]*`/,  // Backtick execution
    /\|\||&&/,  // Command chaining
    /[;&|]\s*(sh|bash|cmd|powershell|pwsh|wget|curl|nc|ncat|python|perl|ruby|php|node|rm|whoami|sudo|cat|ls|find|grep|echo|chmod|chown|mkdir|kill|killall|telnet|scp|ssh|id)\b/i,  // Command word after a separator
    /\b(eval|exec|system|passthru|shell_exec)\b/i,  // Dangerous functions
  ];

  const isThreat = cmdPatterns.some(pattern => pattern.test(input));
  
  if (isThreat && options.log !== false) {
    await logThreat(userId, 'command_injection', 'critical', 'Potential command injection detected', {
      input_length: input.length
    });
  }

  return isThreat;
};

// ============================================
// THREAT CAPSULES (QUARANTINE SYSTEM)
// ============================================

/**
 * Create a threat capsule to quarantine malicious data
 */
export const createThreatCapsule = async (
  userId: string | null,
  threatData: Record<string, any>,
  expiresAt?: Date
): Promise<boolean> => {
  try {
    // Generate hash from threat data
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(threatData)));
    const capsuleHash = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');

    const { error } = await supabase
      .from('threat_capsules')
      .insert({
        user_id: userId,
        capsule_hash: capsuleHash,
        threat_data: threatData,
        isolated: true,
        expires_at: expiresAt || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days default
      });

    if (error) {
      console.error('Failed to create threat capsule:', error);
      return false;
    }

    console.log(`🔒 Threat capsule created: ${capsuleHash.substring(0, 16)}...`);
    return true;
  } catch (error) {
    console.error('Error creating threat capsule:', error);
    return false;
  }
};

/**
 * Get all threat capsules for a user
 */
export const getThreatCapsules = async (userId: string): Promise<any[]> => {
  try {
    const { data, error } = await supabase
      .from('threat_capsules')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to get threat capsules:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching threat capsules:', error);
    return [];
  }
};

/**
 * Release a threat capsule from quarantine
 */
export const releaseThreatCapsule = async (capsuleId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('threat_capsules')
      .update({ isolated: false })
      .eq('id', capsuleId);

    if (error) {
      console.error('Failed to release threat capsule:', error);
      return false;
    }

    console.log(`✅ Threat capsule released: ${capsuleId}`);
    return true;
  } catch (error) {
    console.error('Error releasing threat capsule:', error);
    return false;
  }
};

/**
 * Cleanup expired threat capsules
 */
export const cleanupExpiredCapsules = async (): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('threat_capsules')
      .delete()
      .lt('expires_at', new Date().toISOString());

    if (error) {
      console.error('Failed to cleanup expired capsules:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error cleaning up capsules:', error);
    return false;
  }
};

// ============================================
// RATE LIMITING API
// ============================================

/**
 * Check and update rate limit for an identifier (user_id or IP)
 */
export const checkRateLimit = async (
  identifier: string,
  maxRequests: number = 100,
  windowSeconds: number = 3600
): Promise<{ allowed: boolean; remaining: number }> => {
  try {
    const { data: bucket, error: fetchError } = await supabase
      .from('rate_limit_buckets')
      .select('*')
      .eq('identifier', identifier)
      .maybeSingle();

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Failed to check rate limit:', fetchError);
      return { allowed: true, remaining: maxRequests };
    }

    const now = new Date();
    
    if (!bucket) {
      // Create new bucket
      await supabase
        .from('rate_limit_buckets')
        .insert({
          identifier,
          token_count: maxRequests - 1,
          last_refill: now.toISOString()
        });
      
      return { allowed: true, remaining: maxRequests - 1 };
    }

    const lastRefill = new Date(bucket.last_refill);
    const elapsed = (now.getTime() - lastRefill.getTime()) / 1000;

    // Refill tokens if window has passed
    if (elapsed >= windowSeconds) {
      await supabase
        .from('rate_limit_buckets')
        .update({
          token_count: maxRequests - 1,
          last_refill: now.toISOString()
        })
        .eq('identifier', identifier);
      
      return { allowed: true, remaining: maxRequests - 1 };
    }

    // Check if tokens available
    if (bucket.token_count <= 0) {
      return { allowed: false, remaining: 0 };
    }

    // Decrement token count
    await supabase
      .from('rate_limit_buckets')
      .update({
        token_count: bucket.token_count - 1
      })
      .eq('identifier', identifier);

    return { allowed: true, remaining: bucket.token_count - 1 };
  } catch (error) {
    console.error('Error checking rate limit:', error);
    return { allowed: true, remaining: maxRequests };
  }
};

// ============================================
// THREAT PATTERNS API
// ============================================

/**
 * Get all threat patterns from database
 */
export const getThreatPatterns = async (): Promise<any[]> => {
  try {
    const { data, error } = await supabase
      .from('threat_patterns')
      .select('*')
      .eq('enabled', true)
      .order('severity', { ascending: false });

    if (error) {
      console.error('Failed to get threat patterns:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching threat patterns:', error);
    return [];
  }
};

/**
 * Update a threat pattern
 */
export const updateThreatPattern = async (
  patternId: string,
  updates: { enabled?: boolean; severity?: string; pattern_regex?: string }
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('threat_patterns')
      .update(updates)
      .eq('id', patternId);

    if (error) {
      console.error('Failed to update threat pattern:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating threat pattern:', error);
    return false;
  }
};

// ============================================
// ADVANCED SECURITY STATS
// ============================================

/**
 * Get comprehensive threat statistics
 */
export const getUserThreatStats = async (userId: string): Promise<{
  totalThreats: number;
  threatsByType: Record<string, number>;
  threatsBySeverity: Record<string, number>;
  recentThreats: any[];
  capsulesCount: number;
}> => {
  try {
    const { data: threats } = await supabase
      .from('threat_events')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    const { data: capsules } = await supabase
      .from('threat_capsules')
      .select('id')
      .eq('user_id', userId);

    const threatsByType: Record<string, number> = {};
    const threatsBySeverity: Record<string, number> = {};

    (threats || []).forEach(t => {
      threatsByType[t.threat_type] = (threatsByType[t.threat_type] || 0) + 1;
      threatsBySeverity[t.severity] = (threatsBySeverity[t.severity] || 0) + 1;
    });

    return {
      totalThreats: threats?.length || 0,
      threatsByType,
      threatsBySeverity,
      recentThreats: (threats || []).slice(0, 10),
      capsulesCount: capsules?.length || 0
    };
  } catch (error) {
    console.error('Error getting threat stats:', error);
    return {
      totalThreats: 0,
      threatsByType: {},
      threatsBySeverity: {},
      recentThreats: [],
      capsulesCount: 0
    };
  }
};

/**
 * Get recent security events across all users (admin)
 */
export const getRecentThreats = async (limit: number = 50): Promise<ThreatEvent[]> => {
  try {
    const { data, error } = await supabase
      .from('threat_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Failed to get recent threats:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching recent threats:', error);
    return [];
  }
};

// ============================================
// RATE LIMIT MANAGEMENT
// ============================================

/**
 * Reset rate limit for an identifier
 */
export const resetRateLimit = async (identifier: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('rate_limit_buckets')
      .update({
        token_count: 100,
        last_refill: new Date().toISOString()
      })
      .eq('identifier', identifier);

    if (error) {
      console.error('Failed to reset rate limit:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error resetting rate limit:', error);
    return false;
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Calculate trust tier based on score
 */
export const calculateTier = (score: number): { tier: number; name: string } => {
  if (score >= 86) return { tier: 5, name: 'Armadillo' };
  if (score >= 71) return { tier: 4, name: 'Elevated' };
  if (score >= 51) return { tier: 3, name: 'Trusted' };
  if (score >= 31) return { tier: 2, name: 'Verified' };
  if (score >= 11) return { tier: 1, name: 'Probationary' };
  return { tier: 0, name: 'Untrusted' };
};

/**
 * Get client IP address (approximate)
 */
const getClientIP = async (): Promise<string | null> => {
  // Only providers whitelisted in the Vercel CSP connect-src are used —
  // api.ipify.org is blocked by the Content Security Policy.
  const providers = [
    { url: 'https://ipapi.co/json/', pick: (data: any) => (data && data.ip) || null },
    { url: 'https://ip-api.com/json/', pick: (data: any) => (data && data.query) || null },
  ];

  for (const provider of providers) {
    try {
      const response = await fetch(provider.url);
      if (!response.ok) continue;
      const data = await response.json();
      const ip = provider.pick(data);
      if (ip) return ip;
    } catch {
      // Try the next provider
    }
  }

  return null;
};

/**
 * Get comprehensive security stats for a user
 */
export const getSecurityStats = async (userId: string) => {
  const [trustScore, trustHistory, securityEvents] = await Promise.all([
    getTrustScore(userId),
    getTrustHistory(userId, 10),
    getSecurityEvents(userId, 50)
  ]);

  return {
    trustScore: trustScore?.score || 25,
    trustTier: trustScore?.tier_name || 'Probationary',
    totalEvents: securityEvents.length,
    recentChanges: trustHistory.slice(0, 5),
    lastUpdated: trustScore?.last_updated || null
  };
};

// ============================================
// EXPORT ALL API FUNCTIONS
// ============================================

export const armadilloAPI = {
  // Trust Score (5)
  getTrustScore,
  initializeTrustScore,
  updateTrustScore,
  getTrustHistory,
  calculateTier,
  
  // Security Events (2)
  logSecurityEvent,
  getSecurityEvents,
  
  // Threat Detection (5)
  logThreat,
  checkSQLInjection,
  checkXSS,
  checkPathTraversal,
  checkCommandInjection,
  
  // Threat Capsules (4)
  createThreatCapsule,
  getThreatCapsules,
  releaseThreatCapsule,
  cleanupExpiredCapsules,
  
  // Threat Patterns (2)
  getThreatPatterns,
  updateThreatPattern,
  
  // Advanced Stats (2)
  getUserThreatStats,
  getRecentThreats,
  
  // Rate Limiting (2)
  checkRateLimit,
  resetRateLimit,
  
  // Overall Stats (1)
  getSecurityStats
};

